// @flow

import * as wjcCore from 'wijmo/wijmo';
import * as wjcGrid from 'wijmo/wijmo.grid';

type Options = {
  onRowFreezed?: number => mixed,
  frozenRows?: number,
};

export default class FreezeBarRows {
  flex: wjcGrid.FlexGrid;
  host: HTMLElement;
  dragging = false;
  freezeBar: HTMLElement;
  freeze = -1;
  onRowFreezed: ?(number) => mixed;
  frozenRows: ?number;
  firstRun: ?boolean;
  mouseUpHandler: any => mixed;
  mouseLeaveHandler: any => mixed;

  constructor(options: Options) {
    const { onRowFreezed, frozenRows } = options;

    this.onRowFreezed = onRowFreezed;
    this.frozenRows = frozenRows;
  }

  componentWillUnmount = () => {
    if (
      this.freezeBar &&
      this.freezeBar.parentElement &&
      typeof this.freezeBar.parentElement.removeChild === 'function'
    ) {
      this.freezeBar.parentElement.removeChild(this.freezeBar);
    }

    if (this.flex && this.flex.columns && this.flex.columns.collectionChanged) {
      this.flex.columns.collectionChanged.removeAllHandlers();
    }

    if (this.host && typeof this.host.removeEventListener === 'function') {
      this.host.removeEventListener('mousedown', this.gridMouseDown);
      this.host.removeEventListener('mousemove', this.gridMouseMove);
      this.host.removeEventListener('mouseup', this.mouseUpHandler);
      this.host.removeEventListener('mouseleave', this.mouseLeaveHandler);
    }
  };

  addFreezeBar(flex) {
    const freezeBar = document.createElement('div');
    const host = flex.hostElement;

    this.dragging = false;
    this.flex = flex;
    this.flex.frozenRows = this.frozenRows || 0;
    this.host = host;
    this.freezeBar = freezeBar;

    wjcCore.setCss(freezeBar, {
      cursor: 'row-resize',
      backgroundColor: '#80ADBF',
      opacity: 0.5,
      zIndex: 5,
      position: 'absolute',
      height: 4,
      width: '100%',
      top: 0,
      right: 15,
    });

    try {
      // add handle to flex
      const root = host.querySelector('[wj-part="root"]');
      root.parentElement.appendChild(freezeBar);
      this.positionfreezeBar(flex, freezeBar, flex.frozenRows);

      // attach event handlers
      flex.rows.collectionChanged.addHandler(() => {
        this.positionfreezeBar(flex, freezeBar, flex.frozenRows);
      });
      host.addEventListener('mousedown', this.gridMouseDown, true);
      host.addEventListener('mousemove', this.gridMouseMove, true);

      this.mouseUpHandler = e => {
        this.stopDragging(e);
      };

      this.mouseLeaveHandler = e => {
        if (e.target === host) {
          this.stopDragging(e);
        }
      };

      // stop dragging when the user releases the mouse button
      host.addEventListener('mouseup', this.mouseUpHandler, true);

      // stop dragging when the mouse leaves the control
      host.addEventListener('mouseleave', this.mouseLeaveHandler, true);
    } catch (e) {}
  }

  repositionFreezeBar = () => {
    this.positionfreezeBar(this.flex, this.freezeBar, this.flex.frozenRows);
  };

  gridMouseDown = (e: MouseEvent) => {
    const el = document.elementFromPoint(e.clientX, e.clientY);

    if (
      el === this.freezeBar &&
      this.flex.rows.length > 0 &&
      this.flex.columns.length > 0
    ) {
      // prepare to freeze
      this.flex.frozenRows = 0;
      this.flex.scrollPosition = new wjcCore.Point(
        0,
        this.flex.scrollPosition.y,
      );
      this.dragging = true;
      this.freeze = -1;
      this.host.style.cursor = 'row-resize';

      // done with this event
      e.preventDefault();
      e.stopPropagation();
    }
  };

  gridMouseMove = (e: MouseEvent) => {
    if (this.dragging) {
      // find closest edge
      let minDist = null;

      // eslint-disable-next-line
      for (let i = 0; i <= this.flex.viewRange.row2; i++) {
        const rc = this.flex.getCellBoundingRect(i, 0);
        const dist = Math.abs(rc.top - e.clientY);

        if (minDist == null || dist < minDist) {
          minDist = dist;
          this.freeze = i;

          if (
            this.frozenRows !== this.freeze &&
            typeof this.onRowFreezed === 'function'
          ) {
            this.onRowFreezed(this.freeze);
          }
        }
      }

      // move element to edge
      const forcePosition = this.freeze === 0;
      this.positionfreezeBar(
        this.flex,
        this.freezeBar,
        this.freeze,
        forcePosition,
      );

      // done with this event
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // stop dragging when the button is released or when the mouse leaves the control
  stopDragging(e: any) {
    if (this.dragging) {
      this.dragging = false;
      if (this.freeze > -1) {
        this.flex.frozenRows = this.freeze;
      }
      this.host.style.cursor = '';
      e.preventDefault();
      e.stopPropagation();
    } else {
      this.positionfreezeBar(this.flex, this.freezeBar, this.flex.frozenRows);
    }
  }

  resetFreezeBar = (defaultCol: number = 0) => {
    this.flex.frozenRows = defaultCol;

    this.positionfreezeBar(
      this.flex,
      this.freezeBar,
      this.flex.frozenRows,
      true,
    );
  };

  positionfreezeBar(
    flex,
    div: HTMLElement,
    frozen: number,
    force?: boolean,
  ) {
    const MINIMUM_TOP_POSITION = 29;

    if (
      (flex.columns.length > 0 &&
        flex.rows.length > 0 &&
        this.frozenRows !== frozen) ||
      this.firstRun === undefined ||
      force
    ) {
      this.firstRun = false;
      this.frozenRows = this.flex.frozenRows;

      const cell = flex.getCellBoundingRect(frozen, 0);
      const rect = div.parentElement && div.parentElement.getBoundingClientRect();

      // $FlowFixMe
      cell.top -= rect.top;

      // Sometimes `flex.getCellBoundingRect` doesn't return the appropiate value
      // so in order to safe guard style.top we place a minimun top. This value
      // matches the value when frozenRows === 0
      // eslint-disable-next-line
      div.style.top = `${cell.top < 0 ? MINIMUM_TOP_POSITION : cell.top}px`;
    }
  }
}
